import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../App';
import { getUserCalendar, getAdminCalendar } from '../../services/lmsService';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import Footer from '../Footer';
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  Video, PlayCircle, Clock,
  CheckCircle, XCircle, AlertCircle, ExternalLink, RefreshCw
} from 'lucide-react';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const SmartCalendar = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [events, setEvents] = useState([]);
  const [coursesLegend, setCoursesLegend] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  
  const isAdmin = user?.lms_role === 'admin' || user?.lms_role === 'instructor';
  
  useEffect(() => {
    fetchYearData();
  }, [currentYear, user]);
  
  const fetchYearData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      // Fetch all 12 months data
      const promises = [];
      for (let month = 1; month <= 12; month++) {
        const fetchFn = isAdmin ? getAdminCalendar : getUserCalendar;
        promises.push(fetchFn(month, currentYear).catch(() => ({ data: { events: [] } })));
      }
      
      const results = await Promise.all(promises);
      
      // Combine all events
      let allEvents = [];
      let legend = [];
      let summaryData = { upcoming: 0, completed: 0, cancelled: 0 };
      
      results.forEach((response, index) => {
        if (response.success || response.data) {
          const monthEvents = response.data?.events || [];
          allEvents = [...allEvents, ...monthEvents];
          
          if (response.data?.courses_legend) {
            legend = response.data.courses_legend;
          }
          if (response.data?.summary) {
            summaryData.upcoming += response.data.summary.upcoming || 0;
            summaryData.completed += response.data.summary.completed || 0;
            summaryData.cancelled += response.data.summary.cancelled || 0;
          }
        }
      });
      
      setEvents(allEvents);
      setCoursesLegend(legend);
      setSummary(summaryData);
    } catch (error) {
      console.error('Failed to fetch calendar:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const getDaysInMonth = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Previous month days
    for (let i = 0; i < startingDay; i++) {
      days.push({ date: null, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    
    return days;
  };
  
  const getEventsForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(event => {
      const eventDate = new Date(event.scheduled_date).toISOString().split('T')[0];
      return eventDate === dateStr;
    });
  };
  
  const navigateYear = (direction) => {
    setCurrentYear(prev => prev + direction);
    setSelectedDate(null);
  };
  
  const goToToday = () => {
    const today = new Date();
    setCurrentYear(today.getFullYear());
    setSelectedMonth(today.getMonth());
    setSelectedDate(today);
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'bg-blue-500';
      case 'live': return 'bg-red-500 animate-pulse';
      case 'completed': return 'bg-green-500';
      case 'cancelled': return 'bg-gray-400';
      case 'rescheduled': return 'bg-yellow-500';
      default: return 'bg-gray-400';
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'scheduled': return <Clock className="w-4 h-4" />;
      case 'live': return <Video className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      case 'rescheduled': return <AlertCircle className="w-4 h-4" />;
      default: return <CalendarIcon className="w-4 h-4" />;
    }
  };
  
  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };
  
  const isSelected = (date) => {
    if (!date || !selectedDate) return false;
    return date.toDateString() === selectedDate.toDateString();
  };
  
  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading calendar...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                  <CalendarIcon className="h-7 w-7 sm:h-8 sm:w-8 text-indigo-600" />
                  Smart Class Calendar
                </h1>
                <p className="text-gray-600 text-sm sm:text-base">Track your upcoming classes and sessions</p>
              </div>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <Badge className="bg-indigo-100 text-indigo-700">Admin View</Badge>
                )}
              </div>
            </div>
            
            {/* Year Navigation */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
              <div className="flex items-center gap-2 sm:gap-4">
                <Button variant="outline" size="icon" className="h-8 w-8 sm:h-10 sm:w-10" onClick={() => navigateYear(-1)}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{currentYear}</h2>
                <Button variant="outline" size="icon" className="h-8 w-8 sm:h-10 sm:w-10" onClick={() => navigateYear(1)}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={goToToday}>Today</Button>
              </div>
              
              {/* Summary Stats */}
              <div className="flex flex-wrap gap-4 text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-gray-600">Upcoming: {summary.upcoming || 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-gray-600">Completed: {summary.completed || 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-400"></div>
                  <span className="text-gray-600">Cancelled: {summary.cancelled || 0}</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
            {/* 12 Month Calendar Grid */}
            <div className="lg:col-span-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {MONTHS.map((monthName, monthIndex) => {
                  const days = getDaysInMonth(currentYear, monthIndex);
                  const isCurrentMonth = new Date().getFullYear() === currentYear && new Date().getMonth() === monthIndex;
                  
                  return (
                    <Card 
                      key={monthIndex} 
                      className={`overflow-hidden ${isCurrentMonth ? 'ring-2 ring-indigo-500' : ''}`}
                    >
                      <CardHeader className={`py-2 px-3 ${isCurrentMonth ? 'bg-indigo-50' : 'bg-gray-50'}`}>
                        <CardTitle className={`text-sm font-semibold ${isCurrentMonth ? 'text-indigo-700' : 'text-gray-700'}`}>
                          {monthName}
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-2">
                        {/* Day Headers */}
                        <div className="grid grid-cols-7 gap-0.5 mb-1">
                          {DAYS.map((day, i) => (
                            <div key={i} className="text-center text-xs font-medium text-gray-400 py-0.5">
                              {day}
                            </div>
                          ))}
                        </div>
                        
                        {/* Days Grid */}
                        <div className="grid grid-cols-7 gap-0.5">
                          {days.map((day, i) => {
                            const dayEvents = day.date ? getEventsForDate(day.date) : [];
                            const hasEvents = dayEvents.length > 0;
                            
                            return (
                              <div
                                key={i}
                                className={`aspect-square flex flex-col items-center justify-center text-xs rounded cursor-pointer transition-all ${
                                  !day.isCurrentMonth ? 'text-gray-300' :
                                  isSelected(day.date) ? 'bg-indigo-600 text-white' :
                                  isToday(day.date) ? 'bg-indigo-100 text-indigo-700 font-bold' :
                                  'hover:bg-gray-100'
                                }`}
                                onClick={() => {
                                  if (day.date) {
                                    setSelectedDate(day.date);
                                    setSelectedMonth(monthIndex);
                                  }
                                }}
                              >
                                <span className="text-xs">{day.date?.getDate()}</span>
                                {hasEvents && (
                                  <div className="flex gap-0.5 mt-0.5">
                                    {dayEvents.slice(0, 3).map((event, j) => (
                                      <div
                                        key={j}
                                        className={`w-1 h-1 rounded-full ${
                                          isSelected(day.date) ? 'bg-white' : getStatusColor(event.status)
                                        }`}
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              
              {/* Course Legend */}
              {coursesLegend.length > 0 && (
                <Card className="mt-4">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">Course Legend</CardTitle>
                  </CardHeader>
                  <CardContent className="py-2">
                    <div className="flex flex-wrap gap-3 sm:gap-4">
                      {coursesLegend.map((course, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 sm:w-4 sm:h-4 rounded"
                            style={{ backgroundColor: course.color }}
                          ></div>
                          <span className="text-xs sm:text-sm text-gray-600">{course.title}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            
            {/* Selected Day Events Sidebar */}
            <div className="lg:col-span-1">
              <Card className="sticky top-4">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                    <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                    {selectedDate ? (
                      selectedDate.toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })
                    ) : (
                      'Select a Date'
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!selectedDate ? (
                    <p className="text-gray-500 text-center py-6 sm:py-8 text-sm">
                      Click on a date to view sessions
                    </p>
                  ) : selectedDateEvents.length === 0 ? (
                    <p className="text-gray-500 text-center py-6 sm:py-8 text-sm">
                      No sessions scheduled
                    </p>
                  ) : (
                    <div className="space-y-3 sm:space-y-4 max-h-96 overflow-y-auto">
                      {selectedDateEvents.map((event, i) => (
                        <div
                          key={i}
                          className="p-3 sm:p-4 rounded-lg border-l-4 bg-gray-50 border-gray-300 hover:bg-gray-100 transition-all"
                          style={{ borderLeftColor: event.course_color || undefined }}
                        >
                          <div className="flex items-start justify-between mb-2 gap-2">
                            <div className="min-w-0">
                              <p className="font-medium text-gray-900 text-sm truncate">{event.title}</p>
                              <p className="text-xs text-gray-500 truncate">{event.course_title}</p>
                            </div>
                            <Badge className={`text-xs shrink-0 ${
                              event.status === 'live' ? 'bg-red-100 text-red-700' :
                              event.status === 'completed' ? 'bg-green-100 text-green-700' :
                              event.status === 'cancelled' ? 'bg-gray-100 text-gray-700' :
                              'bg-blue-100 text-blue-700'
                            } capitalize`}>
                              {event.status}
                            </Badge>
                          </div>
                          
                          <div className="space-y-1 text-xs text-gray-600">
                            <div className="flex items-center gap-2">
                              <Clock className="w-3 h-3" />
                              <span>{event.scheduled_time} ({event.duration_minutes}m)</span>
                            </div>
                          </div>
                          
                          {/* Action Buttons */}
                          {event.status === 'live' && event.meeting_link && (
                            <Button
                              className="w-full mt-3 bg-red-600 hover:bg-red-700 text-xs sm:text-sm"
                              size="sm"
                              onClick={() => window.open(event.meeting_link, '_blank')}
                            >
                              <Video className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                              Join Live
                            </Button>
                          )}
                          
                          {event.status === 'completed' && event.recording_url && (
                            <Button
                              variant="outline"
                              className="w-full mt-3 text-xs sm:text-sm"
                              size="sm"
                              onClick={() => window.open(event.recording_url, '_blank')}
                            >
                              <PlayCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                              Watch Recording
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default SmartCalendar;
